/**
 * Campaign Search Utilities
 *
 * Utility functions for validating, sanitizing, and formatting campaign search queries
 */
import { CAMPAIGN_SEARCH_QUERY_MAX_LENGTH } from "@/store/filtersStore";

/**
 * Pattern to match invalid characters (only letters, numbers, and spaces are allowed)
 */
export const INVALID_CHAR_PATTERN = /[^a-zA-Z0-9\s]/g;

/**
 * Debounce delay for search input (in milliseconds)
 */
export const DEBOUNCE_DELAY = 600;

/**
 * Normalizes whitespace in a string by replacing multiple spaces with a single space and trimming
 *
 * @param value - The string to normalize
 * @returns The normalized string
 *
 * @example
 * normalizeWhitespace("hello   world  ") // "hello world"
 */
export const normalizeWhitespace = (value: string): string => {
  return value.replace(/\s+/g, " ").trim();
};

/**
 * Sanitizes a search query by removing invalid characters and normalizing whitespace
 *
 * @param value - The search query to sanitize
 * @returns The sanitized search query
 *
 * @example
 * sanitizeSearchQuery("hello@#world!!  ") // "hello world"
 */
export const sanitizeSearchQuery = (value: string): string => {
  return normalizeWhitespace(value.replace(INVALID_CHAR_PATTERN, " "));
};

/**
 * Extracts invalid characters from a search query
 *
 * @param query - The search query to check
 * @returns An array of unique invalid characters found in the query
 *
 * @example
 * getInvalidCharacters("hello@world#test") // ["@", "#"]
 */
export const getInvalidCharacters = (query: string): string[] => {
  const matches = query.match(INVALID_CHAR_PATTERN);
  return matches ? Array.from(new Set(matches)) : [];
};

/**
 * Checks if a search query contains invalid characters
 *
 * @param query - The search query to check
 * @param trimmedQuery - The trimmed version of the query (optional, will trim if not provided)
 * @returns True if the query contains invalid characters and is not empty
 */
export const hasInvalidCharacters = (query: string, trimmedQuery?: string): boolean => {
  const trimmed = trimmedQuery ?? query.trim();
  const invalidChars = getInvalidCharacters(query);
  return invalidChars.length > 0 && Boolean(trimmed);
};

/**
 * Checks if a sanitized query exceeds the maximum length
 *
 * @param sanitizedQuery - The sanitized query to check
 * @returns True if the query exceeds the maximum length
 */
export const exceedsMaxLength = (sanitizedQuery: string): boolean => {
  return sanitizedQuery.length > CAMPAIGN_SEARCH_QUERY_MAX_LENGTH;
};

/**
 * Generates a validation error message for a search query
 *
 * @param query - The search query to validate
 * @param trimmedQuery - The trimmed version of the query
 * @param sanitizedQuery - The sanitized version of the query
 * @returns A validation error message, or null if valid
 */
export const getValidationError = (query: string, trimmedQuery: string, sanitizedQuery: string): string | null => {
  if (!trimmedQuery) {
    return null;
  }

  if (hasInvalidCharacters(query, trimmedQuery)) {
    return "Invalid characters detected. Only letters, numbers, and spaces are allowed.";
  }

  if (exceedsMaxLength(sanitizedQuery)) {
    return `Only the first ${CAMPAIGN_SEARCH_QUERY_MAX_LENGTH} characters can be searched. Shorten your query to run it.`;
  }

  return null;
};

/**
 * Checks if the target element is editable (input, textarea, or contentEditable)
 *
 * @param target - The target HTML element
 * @returns True if the target is editable
 */
export const isEditableTarget = (target: HTMLElement | null): boolean => {
  if (!target) return false;
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;
};

/**
 * Scrolls to an element with smooth animation
 *
 * @param element - The element to scroll to
 * @param offsetTop - Additional offset from the top (default: 12px)
 */
export const scrollToElement = (element: HTMLElement, offsetTop = 12): void => {
  const rect = element.getBoundingClientRect();
  const target = Math.max(rect.top + window.scrollY - offsetTop, 0);
  window.scrollTo({ top: target, behavior: "smooth" });
};
